import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Lock, Server, Zap, TrendingUp, Wifi, Crown, X, Maximize2, Plus, Minus } from 'lucide-react';
import { LocationData, BackboneLink } from '../types';
import { BACKBONE_FIBER_TYPES, LOCATION_COORDS } from '../constants';
import { WORLD_COUNTRIES, MAP_LON_MIN, MAP_LAT_MAX, MAP_SCALE, MAP_BASE_W, MAP_BASE_H } from '../mapGeo';

interface LocationMapProps {
  locations: Record<string, LocationData>;
  currentLocation: string;
  setCurrentLocation: (key: string) => void;
  unlockLocation: (key: string) => void;
  money: number;
  formatCurrency: (amount: number) => string;
  backboneLinks?: BackboneLink[];
  backboneLinkStats?: Record<string, { usedGbps: number; capacityGbps: number; utilization: number }>;
}

// ── Coordinate system ───────────────────────────────────────────────────────
// Equirectangular world projection — constants come from mapGeo.ts (generated
// together with the coastline data so they can't drift apart).
const BASE_VB = { x: 0, y: 0, w: MAP_BASE_W, h: MAP_BASE_H };

const px = (lon: number, lat: number): [number, number] => [
  (lon - MAP_LON_MIN) * MAP_SCALE,
  (MAP_LAT_MAX - lat) * MAP_SCALE,
];

// ── Land ────────────────────────────────────────────────────────────────────
// Real coastlines from Natural Earth 50m, baked into src/mapGeo.ts (no runtime
// SVG group filters — see CLAUDE.md Operations Map note).
// Countries hosting a datacenter render brighter than the rest of the map.
const GAME_COUNTRIES = new Set([
  'NL', 'DE', 'GB', 'FR', 'ES', 'IT', 'SE', 'PL', 'RO', 'TR',
  'US', 'BR', 'CL', 'AR', 'ZA', 'AE', 'HK', 'JP', 'AU',
]);

// ── City nodes ──────────────────────────────────────────────────────────────
// Coordinates come from LOCATION_COORDS in constants.ts (shared with backbone
// distance calc). lx/ly are label offsets; euCluster labels hide at world zoom.
const CITIES = [
  { key: 'rotterdam',    label: 'Heerlen',      country: 'NL', lx:  10, ly: 14,  euCluster: true },
  { key: 'suburban',     label: 'Rotterdam',    country: 'NL', lx: -10, ly: 14,  euCluster: true },
  { key: 'amsterdam',    label: 'Amsterdam',    country: 'NL', lx:   0, ly: -11, euCluster: true },
  { key: 'industrial',   label: 'Frankfurt',    country: 'DE', lx:  10, ly: 14,  euCluster: true },
  { key: 'london',       label: 'London',       country: 'GB', lx: -10, ly: 14,  euCluster: true },
  { key: 'paris',        label: 'Paris',        country: 'FR', lx:  10, ly: 14,  euCluster: true },
  { key: 'madrid',       label: 'Madrid',       country: 'ES', lx:   0, ly: -11, euCluster: true },
  { key: 'milan',        label: 'Milan',        country: 'IT', lx:  10, ly: 14,  euCluster: true },
  { key: 'stockholm',    label: 'Stockholm',    country: 'SE', lx:  10, ly: 14,  euCluster: true },
  { key: 'warsaw',       label: 'Warsaw',       country: 'PL', lx:  10, ly: 14,  euCluster: true },
  { key: 'bucharest',    label: 'Bucharest',    country: 'RO', lx:  10, ly: 14,  euCluster: true },
  { key: 'istanbul',     label: 'Istanbul',     country: 'TR', lx:   0, ly: -11, euCluster: true },
  { key: 'seattle',      label: 'Seattle',      country: 'US', lx: -10, ly: 14,  euCluster: false },
  { key: 'losangeles',   label: 'Los Angeles',  country: 'US', lx: -10, ly: 14,  euCluster: false },
  { key: 'dallas',       label: 'Dallas',       country: 'US', lx:  10, ly: 14,  euCluster: false },
  { key: 'chicago',      label: 'Chicago',      country: 'US', lx:   0, ly: -11, euCluster: false },
  { key: 'ashburn',      label: 'Ashburn',      country: 'US', lx:  10, ly: 14,  euCluster: false },
  { key: 'newark',       label: 'Newark',       country: 'US', lx:  10, ly: -8,  euCluster: false },
  { key: 'saopaulo',     label: 'São Paulo',    country: 'BR', lx:  10, ly: 14,  euCluster: false },
  { key: 'santiago',     label: 'Santiago',     country: 'CL', lx: -10, ly: 14,  euCluster: false },
  { key: 'buenosaires',  label: 'Buenos Aires', country: 'AR', lx:  10, ly: 14,  euCluster: false },
  { key: 'johannesburg', label: 'Johannesburg', country: 'ZA', lx:  10, ly: 14,  euCluster: false },
  { key: 'dubai',        label: 'Dubai',        country: 'AE', lx:  10, ly: 14,  euCluster: false },
  { key: 'hongkong',     label: 'Hong Kong',    country: 'HK', lx:  10, ly: 14,  euCluster: false },
  { key: 'tokyo',        label: 'Tokyo',        country: 'JP', lx:  10, ly: 14,  euCluster: false },
  { key: 'sydney',       label: 'Sydney',       country: 'AU', lx:  10, ly: 14,  euCluster: false },
].map(c => {
  const [lon, lat] = LOCATION_COORDS[c.key] ?? [0, 0];
  return { ...c, lon, lat };
});

const COUNTRY_NAMES: Record<string, string> = {
  NL:'Netherlands', DE:'Germany', GB:'United Kingdom',
  FR:'France', ES:'Spain', IT:'Italy', SE:'Sweden',
  PL:'Poland', RO:'Romania', TR:'Turkey',
  US:'United States', BR:'Brazil', CL:'Chile', AR:'Argentina',
  ZA:'South Africa', AE:'United Arab Emirates', HK:'Hong Kong',
  JP:'Japan', AU:'Australia',
};

// EU-cluster labels are hidden above this viewBox width (world zoom) to avoid
// a pile-up of overlapping text around Benelux.
const EU_LABEL_MAX_VB_W = 480;

function getServerCount(loc: LocationData): number {
  let n = 0;
  Object.values(loc.floors ?? {}).forEach(f => f.racks?.forEach(r => { n += r.servers?.length ?? 0; }));
  return n;
}
function getDailyRevenue(loc: LocationData): number {
  let rev = 0;
  Object.values(loc.floors ?? {}).forEach(f => f.racks?.forEach(r => r.servers?.forEach(s => {
    if (s.failureStatus === 'failed') return;
    rev += s.revenue * (s.utilizationRate ?? 1) * (s.ageEfficiencyMultiplier ?? 1);
  })));
  return rev * loc.businessMultiplier;
}

// ── Component ────────────────────────────────────────────────────────────────
const FIBER_COLORS: Record<string, string> = {
  '10g': '#3b82f6',
  '100g': '#818cf8',
  '400g': '#a78bfa',
};

const LocationMap: React.FC<LocationMapProps> = ({
  locations, currentLocation, setCurrentLocation, unlockLocation, money, formatCurrency,
  backboneLinks = [],
  backboneLinkStats = {},
}) => {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const defaultHq = useMemo(
    () => CITIES.find(c => locations[c.key]?.unlocked)?.key ?? 'rotterdam',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [hqKey, setHqKey] = useState<string>(defaultHq);

  // ── Pan / zoom state ──
  const [vb, setVb] = useState(BASE_VB);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{ active: boolean; startClient: [number,number]; startVb: typeof BASE_VB; moved: boolean } | null>(null);

  // Zoom helper — zooms toward a normalised point (nx, ny) of the SVG box
  const zoomAt = (factor: number, nx = 0.5, ny = 0.5) => {
    setVb(prev => {
      const aspect = BASE_VB.w / BASE_VB.h;
      const newW = Math.min(Math.max(prev.w * factor, 40), BASE_VB.w);
      const newH = newW / aspect;
      // Keep (nx,ny) point fixed in SVG space
      const cx = prev.x + nx * prev.w;
      const cy = prev.y + ny * prev.h;
      return { x: cx - nx * newW, y: cy - ny * newH, w: newW, h: newH };
    });
  };
  const zoomAtRef = useRef(zoomAt);
  zoomAtRef.current = zoomAt;

  // Wheel zoom — Ctrl/Cmd + scroll only, so plain scrolling still scrolls the
  // page instead of being hijacked by the map. Non-passive for preventDefault.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return; // let the page scroll
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width;
      const ny = (e.clientY - rect.top) / rect.height;
      zoomAtRef.current(e.deltaY > 0 ? 1.15 : 1 / 1.15, nx, ny);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Mouse drag pan
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      active: true,
      startClient: [e.clientX, e.clientY],
      startVb: vb,
      moved: false,
    };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    const d = dragRef.current;
    if (!d?.active) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dx = (e.clientX - d.startClient[0]) / rect.width  * d.startVb.w;
    const dy = (e.clientY - d.startClient[1]) / rect.height * d.startVb.h;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) d.moved = true;
    setVb({
      x: d.startVb.x - dx,
      y: d.startVb.y - dy,
      w: d.startVb.w,
      h: d.startVb.h,
    });
  };

  const onMouseUp = () => {
    if (dragRef.current) dragRef.current.active = false;
  };

  // Click a city dot — only if we didn't drag
  const onCityClick = (key: string, hasDragged: boolean) => {
    if (hasDragged) return;
    const loc = locations[key];
    if (!loc) return;
    setSelectedKey(prev => prev === key ? null : key);
  };

  const cityByKey = useMemo(() => Object.fromEntries(CITIES.map(c => [c.key, c])), []);

  const selectedCity = selectedKey ? cityByKey[selectedKey] : null;
  const selectedLoc  = selectedKey ? locations[selectedKey] : null;

  const viewBoxStr = `${vb.x.toFixed(2)} ${vb.y.toFixed(2)} ${vb.w.toFixed(2)} ${vb.h.toFixed(2)}`;
  const isZoomed = vb.w < BASE_VB.w * 0.98;

  // Scale-aware dot/label sizes — city dots stay a fixed screen size as you zoom
  const dotScale = vb.w / BASE_VB.w; // <1 when zoomed in → scale features UP

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-bold">Operations Map</h2>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isZoomed && (
            <button
              onClick={() => setVb(BASE_VB)}
              className="flex items-center gap-1 px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white"
              title="Reset zoom"
            >
              <Maximize2 size={10} /> Reset
            </button>
          )}
          <span className="flex items-center gap-1.5"><Crown size={9} className="text-amber-400" /> HQ</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Active</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Unlocked</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-600 inline-block" /> Locked</span>
          {backboneLinks.length > 0 && (
            <>
              <span className="text-gray-700">·</span>
              <span className="flex items-center gap-1 text-gray-600">Fiber:</span>
              <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-violet-500" /> Low</span>
              <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-amber-400" /> &gt;50%</span>
              <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-red-500" /> &gt;85%</span>
            </>
          )}
        </div>
      </div>

      {/* Map SVG */}
      <div className="relative bg-[#081220] rounded-xl border border-gray-800 shadow-2xl overflow-hidden">
        {/* Zoom buttons */}
        <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
          <button
            onClick={() => zoomAt(1 / 1.4)}
            className="w-7 h-7 flex items-center justify-center rounded bg-gray-800/80 hover:bg-gray-700 text-gray-400 hover:text-white border border-gray-700"
            title="Zoom in"
          ><Plus size={13} /></button>
          <button
            onClick={() => zoomAt(1.4)}
            className="w-7 h-7 flex items-center justify-center rounded bg-gray-800/80 hover:bg-gray-700 text-gray-400 hover:text-white border border-gray-700"
            title="Zoom out"
          ><Minus size={13} /></button>
        </div>
        <svg
          ref={svgRef}
          viewBox={viewBoxStr}
          className="w-full block select-none"
          style={{ maxHeight: 420, cursor: dragRef.current?.active ? 'grabbing' : 'grab' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {/* Ocean */}
          <rect x={vb.x} y={vb.y} width={vb.w} height={vb.h} fill="#081220" />

          {/* Graticule — every 20° */}
          {[-160,-140,-120,-100,-80,-60,-40,-20,0,20,40,60,80,100,120,140,160].map(lon => {
            const [x] = px(lon, 0);
            return <line key={`lon${lon}`} x1={x} y1={vb.y} x2={x} y2={vb.y + vb.h} stroke="#0d1b2c" strokeWidth={0.5 * dotScale} />;
          })}
          {[-40,-20,0,20,40,60].map(lat => {
            const [,y] = px(0, lat);
            return <line key={`lat${lat}`} x1={vb.x} y1={y} x2={vb.x + vb.w} y2={y} stroke={lat === 0 ? '#122238' : '#0d1b2c'} strokeWidth={0.5 * dotScale} />;
          })}

          {/* Coastal glow — wide soft stroke under the land (filter-free) */}
          {WORLD_COUNTRIES.map((c, i) => (
            <path key={`glow-${i}`} d={c.d}
              fill="none" stroke="#3b6d9e" strokeWidth={3.5 * dotScale}
              strokeLinejoin="round" opacity={0.22}
            />
          ))}

          {/* Land */}
          {WORLD_COUNTRIES.map((c, i) => {
            const isGame = GAME_COUNTRIES.has(c.id);
            return (
              <path key={`land-${i}`} d={c.d}
                fill={isGame ? '#1c3350' : '#13233a'}
                stroke={isGame ? '#3d6a96' : '#264663'}
                strokeWidth={(isGame ? 0.8 : 0.5) * dotScale}
                strokeLinejoin="round"
              />
            );
          })}


          {/* Backbone fiber links */}
          {backboneLinks.map(link => {
            const fromCity = CITIES.find(c => c.key === link.fromLocation);
            const toCity = CITIES.find(c => c.key === link.toLocation);
            if (!fromCity || !toCity) return null;
            const [x1, y1] = px(fromCity.lon, fromCity.lat);
            const [x2, y2] = px(toCity.lon, toCity.lat);
            const ft = BACKBONE_FIBER_TYPES.find(f => f.id === link.fiberTypeId);
            const stats = backboneLinkStats[link.id];
            // Utilization-aware color: fiber tier color at low load → amber → red at saturation
            const util = stats?.utilization ?? 0;
            const utilizationColor = util >= 0.85 ? '#ef4444' : util >= 0.5 ? '#f59e0b' : (FIBER_COLORS[link.fiberTypeId] ?? '#7c3aed');
            const lineWidth = util >= 0.85 ? 2.5 : util >= 0.5 ? 2 : 1.5;
            const usedGbps = stats?.usedGbps ?? 0;
            return (
              <g key={link.id}>
                {/* Glow */}
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={utilizationColor} strokeWidth={5 * dotScale} opacity={0.12} />
                {/* Main line */}
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={utilizationColor} strokeWidth={lineWidth * dotScale} opacity={0.85} strokeDasharray={`${4 * dotScale} ${2 * dotScale}`} />
                {/* Capacity + utilization label at midpoint */}
                <text
                  x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 4 * dotScale}
                  textAnchor="middle" fontSize={7 * dotScale} fill={utilizationColor} opacity={0.95}
                  fontFamily="monospace" fontWeight="bold"
                >
                  {ft?.capacityGbps}G
                </text>
                {stats && usedGbps > 0 && (
                  <text
                    x={(x1 + x2) / 2} y={(y1 + y2) / 2 + 4 * dotScale}
                    textAnchor="middle" fontSize={6 * dotScale} fill={utilizationColor} opacity={0.75}
                    fontFamily="monospace"
                  >
                    {Math.round(util * 100)}%
                  </text>
                )}
              </g>
            );
          })}

          {/* Cities */}
          {CITIES.map(city => {
            const loc = locations[city.key];
            const unlocked  = loc?.unlocked ?? false;
            const isCurrent = city.key === currentLocation;
            const isHq      = city.key === hqKey;
            const isSelected = city.key === selectedKey;
            const [cx2, cy2] = px(city.lon, city.lat);

            const fill   = !loc ? '#0e1c2c' : !unlocked ? '#1a3050' : isCurrent ? '#10b981' : '#3b82f6';
            const stroke = !loc ? '#1a3050' : !unlocked ? '#2d5070' : isCurrent ? '#34d399'  : '#60a5fa';
            const r = (isCurrent ? 5.5 : unlocked ? 5 : 4) * dotScale;

            const fs = 9.5 * dotScale;
            const lx = city.lx * dotScale;
            const ly = city.ly * dotScale;
            // At world zoom the 12 European cities sit on top of each other —
            // only label the interesting ones until the player zooms in.
            const showLabel = !city.euCluster || vb.w < EU_LABEL_MAX_VB_W
              || unlocked || isCurrent || isSelected;

            return (
              <g key={city.key} transform={`translate(${cx2},${cy2})`}
                style={{ cursor: loc ? 'pointer' : 'default' }}
                onMouseDown={e => e.stopPropagation()}
                onMouseUp={e => {
                  e.stopPropagation();
                  onCityClick(city.key, dragRef.current?.moved ?? false);
                  if (dragRef.current) dragRef.current.moved = false;
                }}
              >
                {isCurrent  && <circle r={13 * dotScale} fill="none" stroke="#10b981" strokeWidth={dotScale} opacity="0.15" />}
                {isCurrent  && <circle r={9  * dotScale} fill="none" stroke="#10b981" strokeWidth={dotScale} opacity="0.25" />}
                {isHq       && <circle r={11 * dotScale} fill="none" stroke="#f59e0b" strokeWidth={1.5 * dotScale} opacity="0.4" />}
                {isSelected && !isHq && <circle r={10 * dotScale} fill="none" stroke="#f59e0b" strokeWidth={1.5 * dotScale} opacity="0.7" />}

                <circle r={r} fill={fill} stroke={stroke} strokeWidth={1.2 * dotScale} />

                {isHq && (
                  <text x="0" y={-8 * dotScale} textAnchor="middle" fontSize={8 * dotScale} fill="#fbbf24">★</text>
                )}

                {unlocked && getServerCount(loc) > 0 && (
                  <text x={6 * dotScale} y={-5 * dotScale} fontSize={5.5 * dotScale} fill="#34d399" fontFamily="monospace" fontWeight="bold">
                    {getServerCount(loc)}
                  </text>
                )}

                {showLabel && (
                  <text
                    x={lx} y={ly > 0 ? ly + 2 * dotScale : ly - dotScale}
                    textAnchor={city.lx > 0 ? 'start' : city.lx < 0 ? 'end' : 'middle'}
                    fontSize={fs}
                    fontFamily="'Courier New', monospace"
                    fontWeight={isCurrent ? 'bold' : 'normal'}
                    fill={!loc ? '#22405e' : !unlocked ? '#4a7096' : isCurrent ? '#86efac' : '#93c5fd'}
                    stroke="#081220" strokeWidth={3 * dotScale}
                    style={{ pointerEvents: 'none', paintOrder: 'stroke' }}
                  >
                    {city.label}
                  </text>
                )}
              </g>
            );
          })}

          {/* Compass */}
          <g transform={`translate(${vb.x + vb.w - 18 * dotScale},${vb.y + vb.h - 14 * dotScale})`} opacity="0.25">
            <text x="0" y={-7 * dotScale} textAnchor="middle" fontSize={6.5 * dotScale} fill="#4a6a8a" fontFamily="monospace">N</text>
            <line x1="0" y1={-5 * dotScale} x2="0" y2="0" stroke="#4a6a8a" strokeWidth={0.8 * dotScale} />
            <line x1={-5 * dotScale} y1="0" x2={5 * dotScale} y2="0" stroke="#4a6a8a" strokeWidth={0.8 * dotScale} />
          </g>

          {/* Zoom hint */}
          {!isZoomed && (
            <text x={vb.x + 10 * dotScale} y={vb.y + vb.h - 8 * dotScale} fontSize={9 * dotScale} fill="#2a4a70" fontFamily="monospace">
              ctrl + scroll to zoom · drag to pan
            </text>
          )}
        </svg>
      </div>

      {/* Selected location panel */}
      {selectedCity && selectedLoc && (
        <div className="mt-2 bg-gray-800 border border-gray-700 rounded-xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`w-3 h-3 mt-1 rounded-full shrink-0 ${
                !selectedLoc.unlocked ? 'bg-gray-600' :
                selectedKey === currentLocation ? 'bg-emerald-500' : 'bg-blue-500'
              }`} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-white text-base">{selectedCity.label}</span>
                  <span className="text-xs text-gray-400 font-mono">{COUNTRY_NAMES[selectedCity.country]}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-yellow-400 font-mono">
                    {selectedLoc.businessMultiplier}× revenue
                  </span>
                  {selectedKey === hqKey && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-400 flex items-center gap-1">
                      <Crown size={9} /> HQ
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{selectedLoc.status?.message ?? ''}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 shrink-0">
              {selectedLoc.unlocked ? (
                <>
                  <div className="text-center hidden sm:block">
                    <div className="text-xs text-gray-500 flex items-center gap-1"><Server size={9} /> Servers</div>
                    <div className="text-sm font-bold">{getServerCount(selectedLoc)}</div>
                  </div>
                  <div className="text-center hidden sm:block">
                    <div className="text-xs text-gray-500 flex items-center gap-1"><TrendingUp size={9} /> Rev/day</div>
                    <div className="text-sm font-bold text-emerald-400">{formatCurrency(getDailyRevenue(selectedLoc))}</div>
                  </div>
                  <div className="text-center hidden sm:block">
                    <div className="text-xs text-gray-500 flex items-center gap-1"><Zap size={9} /> Power</div>
                    <div className="text-sm font-bold text-red-400">{(selectedLoc.usedPower ?? 0).toFixed(1)} kW</div>
                  </div>
                  <div className="text-center hidden sm:block">
                    <div className="text-xs text-gray-500 flex items-center gap-1"><Wifi size={9} /> MMR</div>
                    <div className="text-sm font-bold">{selectedLoc.mmr?.built ? <span className="text-emerald-400">✓</span> : <span className="text-gray-600">—</span>}</div>
                  </div>
                  <div className="flex gap-2">
                    {selectedKey !== currentLocation && (
                      <button
                        onClick={() => { setCurrentLocation(selectedKey!); setSelectedKey(null); }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-700 hover:bg-blue-600 text-white"
                      >Switch →</button>
                    )}
                    {selectedKey !== hqKey && (
                      <button
                        onClick={() => setHqKey(selectedKey!)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-800 hover:bg-amber-700 text-amber-200 flex items-center gap-1"
                      >
                        <Crown size={10} /> Set HQ
                      </button>
                    )}
                  </div>
                </>
              ) : selectedLoc.unlockCost ? (
                <button
                  disabled={money < selectedLoc.unlockCost}
                  onClick={() => { unlockLocation(selectedKey!); setSelectedKey(null); }}
                  className="px-4 py-2 rounded-lg text-sm font-bold bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white whitespace-nowrap"
                >
                  <Lock size={12} className="inline mr-1.5" />
                  Unlock — {formatCurrency(selectedLoc.unlockCost)}
                  {money < selectedLoc.unlockCost && (
                    <span className="block text-[10px] font-normal opacity-70 text-center">
                      need {formatCurrency(selectedLoc.unlockCost - money)} more
                    </span>
                  )}
                </button>
              ) : null}

              <button onClick={() => setSelectedKey(null)} className="text-gray-600 hover:text-white">
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationMap;
